import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { donorsService } from '../services/api';
import './DonorImportModal.css';

const DonorImportModal = ({ onClose, onImport }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [importedCount, setImportedCount] = useState(0);
  const [duplicateResolutions, setDuplicateResolutions] = useState({});
  const [stage, setStage] = useState('upload'); // 'upload' or 'resolve'

  const onDrop = (acceptedFiles) => {
    setError(null);
    setSuccess(null);
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv']
    },
    maxFiles: 1,
    disabled: loading || stage === 'resolve'
  });

  const handleFirstSubmit = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await donorsService.importInitial(formData);
      
      setImportedCount(response.data.imported);
      
      if (response.data.duplicates && response.data.duplicates.length > 0) {
        setDuplicates(response.data.duplicates);
        setStage('resolve');
        // Initialize resolutions for each duplicate
        const initialResolutions = {};
        response.data.duplicates.forEach((dup, index) => {
          initialResolutions[index] = 'skip'; // default action
        });
        setDuplicateResolutions(initialResolutions);
      } else {
        setSuccess(`Successfully imported ${response.data.imported} donors!`);
        setTimeout(() => {
          onImport();
          onClose();
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to import donors');
      console.error('Import error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResolutionChange = (index, action) => {
    setDuplicateResolutions(prev => ({
      ...prev,
      [index]: action
    }));
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const resolutionData = duplicates.map((dup, index) => ({
        importData: dup.importData,
        existingDonor: dup.existingDonor,
        action: duplicateResolutions[index]
      }));

      const response = await donorsService.importResolve(resolutionData);
      
      const totalImported = importedCount + (response.data.additionalImported || 0);
      setSuccess(`Successfully imported ${totalImported} donors! ${response.data.updated || 0} donors updated.`);
      
      setTimeout(() => {
        onImport();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process duplicates');
      console.error('Resolution error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal donor-import-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Import Donors from CSV</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {stage === 'upload' && (
            <>
              <div className="import-instructions">
                <h3>Instructions</h3>
                <p>Your CSV file should include the following columns:</p>
                <ul>
                  <li><strong>first_name</strong> - First name of the donor (required)</li>
                  <li><strong>last_name</strong> - Last name of the donor (required)</li>
                  <li><strong>email</strong> - Email address</li>
                  <li><strong>phone</strong> - Phone number</li>
                  <li><strong>address</strong> - Street address</li>
                  <li><strong>city</strong> - City</li>
                  <li><strong>state</strong> - State</li>
                  <li><strong>zip</strong> - ZIP code</li>
                </ul>
                <p className="note">
                  <strong>Note:</strong> The system will check for duplicate names and allow you to resolve conflicts.
                </p>
              </div>

              <div
                {...getRootProps()}
                className={`dropzone ${isDragActive ? 'active' : ''} ${loading ? 'disabled' : ''}`}
              >
                <input {...getInputProps()} />
                <div className="dropzone-content">
                  {selectedFile ? (
                    <>
                      <p className="dropzone-text">✓ {selectedFile.name}</p>
                      <p className="dropzone-hint">Click to select a different file</p>
                    </>
                  ) : isDragActive ? (
                    <p>Drop the CSV file here...</p>
                  ) : (
                    <>
                      <p className="dropzone-text">Drag and drop a CSV file here, or click to select</p>
                      <p className="dropzone-hint">Only CSV files are accepted</p>
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {stage === 'resolve' && (
            <div className="duplicate-resolution">
              <div className="resolution-header">
                <h3>Duplicate Donors Found</h3>
                <p>
                  {importedCount} donors imported successfully. 
                  Please review the {duplicates.length} potential duplicate(s) below and choose how to handle each one.
                </p>
              </div>

              <div className="duplicates-list">
                {duplicates.map((duplicate, index) => (
                  <div key={index} className="duplicate-item">
                    <div className="duplicate-comparison">
                      <div className="duplicate-column existing">
                        <h4>Existing Donor</h4>
                        <div className="donor-info">
                          <p><strong>Name:</strong> {duplicate.existingDonor.first_name} {duplicate.existingDonor.last_name}</p>
                          <p><strong>Email:</strong> {duplicate.existingDonor.email || 'N/A'}</p>
                          <p><strong>Phone:</strong> {duplicate.existingDonor.phone || 'N/A'}</p>
                          <p><strong>Address:</strong> {duplicate.existingDonor.address ? 
                            `${duplicate.existingDonor.address.street || ''}, ${duplicate.existingDonor.address.city || ''}, ${duplicate.existingDonor.address.state || ''} ${duplicate.existingDonor.address.zip || ''}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '') 
                            : 'N/A'}</p>
                        </div>
                      </div>

                      <div className="duplicate-column import">
                        <h4>Import Data</h4>
                        <div className="donor-info">
                          <p><strong>Name:</strong> {duplicate.importData.first_name} {duplicate.importData.last_name}</p>
                          <p><strong>Email:</strong> {duplicate.importData.email || 'N/A'}</p>
                          <p><strong>Phone:</strong> {duplicate.importData.phone || 'N/A'}</p>
                          <p><strong>Address:</strong> {
                            [duplicate.importData.address, duplicate.importData.city, duplicate.importData.state, duplicate.importData.zip]
                              .filter(Boolean).join(', ') || 'N/A'
                          }</p>
                        </div>
                      </div>
                    </div>

                    <div className="resolution-options">
                      <label>
                        <input
                          type="radio"
                          name={`resolution-${index}`}
                          value="skip"
                          checked={duplicateResolutions[index] === 'skip'}
                          onChange={() => handleResolutionChange(index, 'skip')}
                        />
                        <span>Don't Import (keep existing donor)</span>
                      </label>
                      <label>
                        <input
                          type="radio"
                          name={`resolution-${index}`}
                          value="import"
                          checked={duplicateResolutions[index] === 'import'}
                          onChange={() => handleResolutionChange(index, 'import')}
                        />
                        <span>Import Anyway (create separate donor)</span>
                      </label>
                      <label>
                        <input
                          type="radio"
                          name={`resolution-${index}`}
                          value="update"
                          checked={duplicateResolutions[index] === 'update'}
                          onChange={() => handleResolutionChange(index, 'update')}
                        />
                        <span>Update Existing (overwrite with import data)</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          {stage === 'upload' ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleFirstSubmit}
              disabled={loading || !selectedFile}
            >
              {loading ? 'Processing...' : 'Import'}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleFinalSubmit}
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Complete Import'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DonorImportModal;
